/*
  LetterCompose — the AI-assisted letter builder.

  Two modes:
    - /letters/new       → start from scratch
    - /letters/:letterId → load and edit a saved draft

  Flow:
    1. Pick client (email autocomplete) + template
    2. Pick recipient (GP dropdown OR free-text NDIS coordinator)
    3. For NDIS letters: fill header fields (NDIS number, plan dates, etc.)
    4. Toggle which data sources to pull in
    5. Tap "Draft with AI" → Kimi K2 produces a Markdown draft
    6. Edit inline (textarea), then Save / Download DOCX / Download PDF /
       Email via Resend
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Sparkles, Save, FileDown, Send, Mail, UserPlus, Loader2, Trash2, Copy, Printer, Eye,
} from "lucide-react";
import { printPdfBlob } from "@/lib/printPdf";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useActiveClient } from "@/lib/activeClient";
import { MeasurementSourcePreview } from "@/components/letters/MeasurementSourcePreview";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LetterCompose() {
  const { letterId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !letterId;
  const { client: activeClient, setClient: setActiveClient } = useActiveClient();

  const [templates, setTemplates] = useState([]);
  const [gps, setGps] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [gpDialogOpen, setGpDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Letter state
  const [form, setForm] = useState({
    id: null,
    client_email: searchParams.get("client_email") || "",
    client_name: searchParams.get("client_name") || "",
    template_id: "gp_care_plan_update",
    subject: "",
    body_markdown: "",
    recipient_gp_id: "",
    recipient_snapshot: null,
    header_fields: {},
    sources: { intake: false, soap_notes: true, questionnaires: true, sbn: true, appointments: true },
    extra_notes: "",
    diagnosis_hint: "",
    signature_id: "",
    // "letter" (default) → formal printed letter with address block +
    // "Dear …," / "Yours sincerely,". "email" → email body: no address
    // block, "Hi …,", tight paragraphs, "Kind regards,".
    delivery_format: "letter",
    status: "draft",
  });
  const [contextSummary, setContextSummary] = useState(null);
  const [measurement, setMeasurement] = useState(null);
  const [modelUsed, setModelUsed] = useState(null);
  // Uploaded patient_attachments IDs the therapist wants folded into
  // the LLM prompt so the drafted letter can reference their content
  // (e.g. imaging report, GP referral). Loaded lazily when the client
  // email is known.
  const [clientDocs, setClientDocs] = useState([]);
  const [clientDocsLoading, setClientDocsLoading] = useState(false);
  const [refAttachmentIds, setRefAttachmentIds] = useState(() => new Set());
  const [refExtractSummary, setRefExtractSummary] = useState([]);
  const [signatures, setSignatures] = useState([]);

  useEffect(() => {
    api.get("/settings/signatures")
      .then((r) => setSignatures(r.data?.signatures || []))
      .catch(() => setSignatures([]));
  }, []);

  const currentTemplate = useMemo(
    () => templates.find((t) => t.id === form.template_id),
    [templates, form.template_id],
  );

  // Compute a sensible default subject line from the current template +
  // client name — e.g. "GP Care Plan Update re: Jane Doe". Used to
  // auto-populate the Subject field when the user hasn't touched it.
  const defaultSubject = useMemo(() => {
    const name = (form.client_name || "").trim();
    const tplName = (currentTemplate?.name || "").trim();
    if (!tplName || !name) return "";
    // Match user request: "GP Update re: [Patient Name]"
    const short = tplName.replace(/^GP Care Plan Update$/i, "GP Update");
    return `${short} re: ${name}`;
  }, [currentTemplate, form.client_name]);

  // Track the last subject we auto-populated so we only overwrite when
  // the user hasn't manually edited it.
  const [lastAutoSubject, setLastAutoSubject] = useState("");

  // Auto-populate subject when template or client changes — unless the
  // therapist has typed their own subject.
  useEffect(() => {
    if (!defaultSubject) return;
    setForm((f) => {
      const current = (f.subject || "").trim();
      if (current === "" || current === lastAutoSubject) {
        return { ...f, subject: defaultSubject };
      }
      return f;
    });
    setLastAutoSubject(defaultSubject);
  }, [defaultSubject]);

  // Register the client we're writing for as the "active client" so the
  // header badge and back-breadcrumb work when the therapist opened this
  // screen via a bookmark or the global /letters list.
  useEffect(() => {
    const email = (form.client_email || "").trim().toLowerCase();
    if (!email) return;
    setActiveClient({ email, name: form.client_name || "" });
  }, [form.client_email, form.client_name, setActiveClient]);

  const refreshGps = useCallback(async () => {
    const r = await api.get("/letters/gps");
    setGps(r.data.gps || []);
  }, []);

  // Initial load — templates, GPs, and (if editing) the letter itself.
  useEffect(() => {
    (async () => {
      try {
        const [tpl, gpsR] = await Promise.all([
          api.get("/letters/templates"),
          api.get("/letters/gps"),
        ]);
        setTemplates(tpl.data.templates || []);
        setGps(gpsR.data.gps || []);
        if (letterId) {
          const l = await api.get(`/letters/${letterId}`);
          // Cross-reference the client's live display name — a letter
          // drafted months ago snapshots the client_name at that
          // moment, so if the operator renamed the client in the
          // meantime the draft would still show the old name. When we
          // have a client_email, pull the current profile's
          // ``full_name`` (backend keeps this in sync with the
          // split first/middle/last fields) and prefer it over the
          // stored snapshot. Silent on error — a letter with no
          // matching profile still opens correctly with its stored
          // name.
          let liveName = l.data.client_name || "";
          const em = (l.data.client_email || "").trim();
          if (em) {
            try {
              const p = await api.get(
                `/clients/${encodeURIComponent(em)}/profile`,
              );
              const fresh = (p.data?.profile?.full_name || "").trim();
              if (fresh) liveName = fresh;
            } catch { /* profile 404 / no override — keep the letter's snapshot */ }
          }
          setForm({
            id: l.data.id,
            client_email: l.data.client_email || "",
            client_name: liveName,
            template_id: l.data.template_id,
            subject: l.data.subject || "",
            body_markdown: l.data.body_markdown || "",
            recipient_gp_id: l.data.recipient_gp_id || "",
            recipient_snapshot: l.data.recipient_snapshot || null,
            header_fields: l.data.header_fields || {},
            sources: l.data.sources || { soap_notes: true, questionnaires: true, sbn: true, appointments: true },
            extra_notes: l.data.extra_notes || "",
            diagnosis_hint: "",
            signature_id: l.data.signature_id || "",
            delivery_format: l.data.delivery_format || "letter",
            status: l.data.status || "draft",
          });
        }
        // /letters/new path — client_name comes from URL params which
        // may be stale if the operator renamed the client in another
        // tab. Cross-reference the live profile so the header is right
        // from the first render.
        if (!letterId) {
          const em = (searchParams.get("client_email") || "").trim();
          if (em) {
            try {
              const p = await api.get(
                `/clients/${encodeURIComponent(em)}/profile`,
              );
              const fresh = (p.data?.profile?.full_name || "").trim();
              if (fresh) {
                setForm((f) => ({ ...f, client_name: fresh }));
              }
            } catch { /* profile 404 — keep the URL-supplied name */ }
          }
        }
      } catch (e) {
        toast.error("Failed to load", { description: e?.response?.data?.detail || e.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [letterId]);

  // When the user switches templates, seed the default source toggles.
  useEffect(() => {
    if (!isNew) return;
    if (!currentTemplate) return;
    setForm((f) => ({ ...f, sources: { measurements: !!measurement?.available, ...(currentTemplate.default_sources || {}) } }));
  }, [isNew, currentTemplate]);

  // Body-measurement progress preview; ticked by default on new letters when 2+ assessments exist.
  useEffect(() => {
    const email = (form.client_email || "").trim().toLowerCase();
    if (!email) { setMeasurement(null); return; }
    const t = setTimeout(() => {
      api.get("/letters/measurement-progress", {
        params: {
          client_email: email,
          client_name: form.client_name || undefined,
          canonical_client_id: searchParams.get("canonical_client_id") || undefined,
        },
      })
        .then((r) => {
          setMeasurement(r.data);
          if (isNew) setForm((f) => ({ ...f, sources: { ...f.sources, measurements: !!r.data?.available } }));
        })
        .catch(() => setMeasurement(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.client_email, form.client_name, isNew]);

  // Fetch the client's uploaded documents whenever the email changes.
  // Feeds the "Reference documents" picker.
  useEffect(() => {
    const email = (form.client_email || "").trim().toLowerCase();
    if (!email) { setClientDocs([]); return; }
    setClientDocsLoading(true);
    api.get(`/health-record/${encodeURIComponent(email)}/attachments`)
      .then((r) => setClientDocs(r.data?.attachments || []))
      .catch(() => setClientDocs([]))
      .finally(() => setClientDocsLoading(false));
  }, [form.client_email]);

  function toggleRefAttachment(id) {
    setRefAttachmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }


  function selectGp(gpId) {
    const gp = gps.find((g) => g.id === gpId) || null;
    setForm((f) => ({ ...f, recipient_gp_id: gpId, recipient_snapshot: gp }));
  }

  async function draftWithAi() {
    if (!form.client_email.trim()) {
      toast.error("Enter the client's email first.");
      return;
    }
    setDrafting(true);
    try {
      const r = await api.post("/letters/compose", {
        client_email: form.client_email.trim(),
        client_name: form.client_name,
        template_id: form.template_id,
        sources: form.sources,
        recipient_gp_id: form.recipient_gp_id || null,
        header_fields: form.header_fields,
        extra_notes: form.extra_notes,
        diagnosis_hint: form.diagnosis_hint,
        delivery_format: form.delivery_format || "letter",
        reference_attachment_ids: Array.from(refAttachmentIds),
        canonical_client_id: searchParams.get("canonical_client_id") || null,
      });
      setForm((f) => ({
        ...f,
        body_markdown: r.data.markdown,
        recipient_snapshot: r.data.recipient_snapshot || f.recipient_snapshot,
      }));
      setContextSummary(r.data.context_summary || null);
      setModelUsed(r.data.model || null);
      setRefExtractSummary(r.data.reference_documents || []);
      const failed = (r.data.reference_documents || []).filter((d) => !d.ok);
      if (failed.length) {
        toast.warning(
          `Draft ready — ${failed.length} reference document${failed.length === 1 ? "" : "s"} could not be read`,
          { description: failed.map((f) => `${f.filename || f.id}: ${f.reason}`).join("; ") },
        );
      } else {
        toast.success("Draft ready — review and edit below.");
      }
    } catch (e) {
      toast.error("Draft failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setDrafting(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      if (form.id) {
        const r = await api.patch(`/letters/${form.id}`, {
          body_markdown: form.body_markdown,
          subject: form.subject,
          // Persist the live-synced display name back onto the letter
          // so exports + list search + duplicate-from-past reflect the
          // client's CURRENT name — not the snapshot from when the
          // draft was first created.
          client_name: form.client_name,
          recipient_snapshot: form.recipient_snapshot,
          header_fields: form.header_fields,
          extra_notes: form.extra_notes,
          signature_id: form.signature_id || null,
          delivery_format: form.delivery_format || "letter",
          status: form.status,
        });
        setForm((f) => ({ ...f, ...r.data, id: r.data.id }));
      } else {
        const r = await api.post("/letters", {
          client_email: form.client_email,
          client_name: form.client_name,
          template_id: form.template_id,
          subject: form.subject,
          body_markdown: form.body_markdown,
          recipient_gp_id: form.recipient_gp_id || null,
          recipient_snapshot: form.recipient_snapshot,
          header_fields: form.header_fields,
          sources: form.sources,
          extra_notes: form.extra_notes,
          signature_id: form.signature_id || null,
          delivery_format: form.delivery_format || "letter",
          // Stamp the sibling on a shared email so the whole letter
          // pipeline (list filter, profile-override merge on export,
          // outbound CC lookup) sticks to *this* person. Absent →
          // backend auto-resolves when the email has exactly one
          // canonical row.
          canonical_client_id: searchParams.get("canonical_client_id") || null,
        });
        setForm((f) => ({ ...f, id: r.data.id }));
        navigate(`/letters/${r.data.id}`, { replace: true });
      }
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  // Extract a human-readable error description from an axios failure
  // that was requested with ``responseType: "blob"``. When the server
  // returns a non-2xx, the response body is still a Blob (the raw
  // JSON), not a parsed object — so ``err.response.data.detail`` is
  // ``undefined`` and the toast reads useless. Decode the blob,
  // parse the JSON, lift the ``detail`` field.
  async function describeBlobError(err, fallback) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        try {
          const j = JSON.parse(text);
          return j?.detail || text || fallback;
        } catch (_parseErr) {
          return text || fallback;
        }
      } catch (_blobErr) {
        return fallback;
      }
    }
    return data?.detail || err?.message || fallback;
  }

  async function openPreview() {
    if (!form.id) {
      toast.error("Save the letter first, then preview.");
      return;
    }
    setPreviewLoading(true);
    try {
      const r = await api.get(`/letters/${form.id}/export.pdf`, { responseType: "blob" });
      // Revoke any previous preview URL to free memory, then make a
      // fresh object URL for the <iframe> inside the dialog.
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(r.data);
      setPreviewUrl(url);
    } catch (e) {
      const detail = await describeBlobError(e, "Preview failed");
      toast.error("Preview failed", { description: detail });
    } finally {
      setPreviewLoading(false);
    }
  }

  function closePreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }

  async function download(kind) {
    if (!form.id) {
      toast.error("Save the letter first, then download.");
      return;
    }
    setDownloading(kind);
    try {
      // Fetch as blob so the browser downloads it.
      const r = await api.get(`/letters/${form.id}/export.${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = suggestFilename(form, kind);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const detail = await describeBlobError(e, `${kind.toUpperCase()} export failed`);
      toast.error(`${kind.toUpperCase()} export failed`, { description: detail });
    } finally {
      setDownloading(null);
    }
  }

  async function print() {
    if (!form.id) {
      toast.error("Save the letter first, then print.");
      return;
    }
    setPrinting(true);
    try {
      const r = await api.get(`/letters/${form.id}/export.pdf`, { responseType: "blob" });
      await printPdfBlob(r.data);
    } catch (e) {
      const detail = await describeBlobError(e, "Print failed");
      toast.error("Print failed", { description: detail });
    } finally {
      setPrinting(false);
    }
  }

  async function sendEmail(payload) {
    if (!form.id) {
      toast.error("Save the letter first, then email.");
      return;
    }
    setEmailing(true);
    try {
      const r = await api.post(`/letters/${form.id}/send-email`, payload);
      if (r.data.ok) {
        toast.success("Email sent", { description: `Log id ${r.data.log_id}` });
        setForm((f) => ({ ...f, status: "sent" }));
        setEmailOpen(false);
      } else {
        toast.error("Send failed", { description: r.data.error });
      }
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setEmailing(false);
    }
  }

  async function del() {
    if (!form.id) return;
    if (!window.confirm("Delete this letter draft? This cannot be undone.")) return;
    try {
      await api.delete(`/letters/${form.id}`);
      toast.success("Deleted");
      navigate("/letters");
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function duplicate() {
    if (!form.id) return;
    try {
      const r = await api.post(`/letters/${form.id}/duplicate`);
      toast.success("Duplicated — opening the new draft");
      navigate(`/letters/${r.data.id}`);
    } catch (e) {
      toast.error("Duplicate failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  if (loading) {
    return <div className="p-10 text-center text-sm text-zinc-500">Loading…</div>;
  }

  return (
    <div className="mx-auto max-w-[1400px] px-6 sm:px-8 md:px-10 py-8">
      <div className="flex items-center gap-4 flex-wrap text-xs">
        <button
          onClick={() => navigate("/letters")}
          className="inline-flex items-center gap-1 text-zinc-600 hover:text-zinc-950"
          data-testid="compose-back-btn"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to letters
        </button>
        {activeClient?.email && (
          <button
            onClick={() =>
              navigate(`/clients/${encodeURIComponent(activeClient.email)}`)
            }
            className="inline-flex items-center gap-1 text-emerald-800 hover:text-emerald-900 border border-emerald-200 bg-emerald-50 rounded-full px-2.5 py-1 font-medium"
            data-testid="compose-back-to-client"
            title={`Back to ${activeClient.name || activeClient.email}`}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to {activeClient.name || activeClient.email}
          </button>
        )}
      </div>

      <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> {isNew ? "New letter" : "Edit letter"}
          </div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            {form.client_name || form.client_email || "Compose"}
          </h1>
        </div>
        {form.id && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={duplicate}
              className="border-zinc-300"
              data-testid="letter-dup-btn"
              title="Clone this letter as a fresh draft"
            >
              <Copy className="h-4 w-4 mr-1.5" /> Duplicate
            </Button>
            <Button
              variant="outline"
              onClick={del}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="letter-del-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete draft
            </Button>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — controls */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-3" data-testid="compose-client-card">
            <SectionTitle>Client</SectionTitle>
            <Row label="Client email">
              <Input
                value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                placeholder="client@example.com"
                data-testid="compose-client-email"
                className="border-zinc-300"
              />
            </Row>
            <Row label="Client name">
              <Input
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                placeholder="Full name for the letter"
                data-testid="compose-client-name"
                className="border-zinc-300"
              />
            </Row>
          </Card>

          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-3" data-testid="compose-template-card">
            <SectionTitle>Template</SectionTitle>
            <Select
              value={form.template_id}
              onValueChange={(v) => setForm({ ...form, template_id: v })}
            >
              <SelectTrigger className="border-zinc-300" data-testid="compose-template-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentTemplate && (
              <p className="text-2xs text-zinc-500 leading-relaxed">{currentTemplate.description}</p>
            )}
          </Card>

          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-3" data-testid="compose-recipient-card">
            <div className="flex items-center justify-between">
              <SectionTitle>Recipient</SectionTitle>
              <Button
                variant="ghost" size="sm"
                onClick={() => setGpDialogOpen(true)}
                data-testid="compose-add-gp"
                className="h-7 text-xs"
              >
                <UserPlus className="h-3.5 w-3.5 mr-1" /> New GP
              </Button>
            </div>
            {currentTemplate?.recipient_type === "ndis" ? (
              <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded p-2">
                NDIS letters are addressed to <b>the NDIA / Plan Manager</b> — no GP recipient needed.
              </div>
            ) : (
              <>
                <Select value={form.recipient_gp_id} onValueChange={selectGp}>
                  <SelectTrigger className="border-zinc-300" data-testid="compose-gp-select">
                    <SelectValue placeholder="Pick a GP…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {gps.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.title} {g.first_name} {g.last_name} · {g.clinic}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.recipient_snapshot && (
                  <div className="text-2xs text-zinc-600 leading-relaxed border border-zinc-200 rounded p-2 bg-zinc-50" data-testid="compose-recipient-preview">
                    {form.recipient_snapshot.title} {form.recipient_snapshot.first_name} {form.recipient_snapshot.last_name}<br />
                    {form.recipient_snapshot.clinic}<br />
                    {form.recipient_snapshot.address_line_1}
                    {form.recipient_snapshot.address_line_2 ? <><br />{form.recipient_snapshot.address_line_2}</> : null}
                    <br />
                    {form.recipient_snapshot.suburb} {form.recipient_snapshot.state} {form.recipient_snapshot.postcode}
                  </div>
                )}
              </>
            )}
          </Card>

          {(currentTemplate?.header_fields || []).length > 0 && (
            <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-3" data-testid="compose-header-fields">
              <SectionTitle>Header fields (NDIS)</SectionTitle>
              {currentTemplate.header_fields.map((f) => (
                <Row key={f.id} label={f.label}>
                  <Input
                    value={form.header_fields[f.id] || ""}
                    onChange={(e) => setForm({
                      ...form,
                      header_fields: { ...form.header_fields, [f.id]: e.target.value },
                    })}
                    className="border-zinc-300"
                    data-testid={`compose-hf-${f.id}`}
                  />
                </Row>
              ))}
            </Card>
          )}

          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-2" data-testid="compose-sources-card">
            <SectionTitle>Data sources to include</SectionTitle>
            <SourceToggle
              label="Latest intake form" value={form.sources.intake}
              onChange={(v) => setForm({ ...form, sources: { ...form.sources, intake: v } })}
              testId="src-intake"
            />
            <SourceToggle
              label="Last 3 SOAP notes" value={form.sources.soap_notes}
              onChange={(v) => setForm({ ...form, sources: { ...form.sources, soap_notes: v } })}
              testId="src-soap"
            />
            <SourceToggle
              label="Recent questionnaires (PDQ-39, ONERO)" value={form.sources.questionnaires}
              onChange={(v) => setForm({ ...form, sources: { ...form.sources, questionnaires: v } })}
              testId="src-quest"
            />
            <SourceToggle
              label="Strength by Numbers session" value={form.sources.sbn}
              onChange={(v) => setForm({ ...form, sources: { ...form.sources, sbn: v } })}
              testId="src-sbn"
            />
            <SourceToggle
              label="Appointment history (12mo)" value={form.sources.appointments}
              onChange={(v) => setForm({ ...form, sources: { ...form.sources, appointments: v } })}
              testId="src-appts"
            />
            <SourceToggle
              label="Body measurements progress (first vs latest)" value={!!form.sources.measurements}
              onChange={(v) => setForm({ ...form, sources: { ...form.sources, measurements: v } })}
              testId="src-measurements"
              disabled={!measurement?.available}
            />
            <MeasurementSourcePreview summary={measurement} enabled={!!form.sources.measurements} />
          </Card>

          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-2" data-testid="compose-reference-docs-card">
            <SectionTitle>
              Reference documents from client record
              {refAttachmentIds.size > 0 && (
                <span className="ml-2 text-2xs text-emerald-700 font-medium normal-case tracking-normal">
                  {refAttachmentIds.size} selected
                </span>
              )}
            </SectionTitle>
            <p className="text-2xs text-zinc-500 -mt-1">
              Tick any uploaded PDF and its text will be read by the AI and folded into the draft — helpful for referencing imaging reports, GP referrals, or discharge summaries.
            </p>
            {clientDocsLoading ? (
              <div className="text-xs text-zinc-500 italic">Loading files…</div>
            ) : !form.client_email ? (
              <div className="text-xs text-zinc-500 italic">Enter a client email first.</div>
            ) : clientDocs.length === 0 ? (
              <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-2">
                No files uploaded to this client&apos;s record yet.
              </div>
            ) : (
              <ul
                className="border border-zinc-200 rounded-md divide-y divide-zinc-200 max-h-56 overflow-y-auto"
                data-testid="compose-reference-docs-list"
              >
                {clientDocs.map((a) => {
                  const active = refAttachmentIds.has(a.id);
                  const summary = refExtractSummary.find((r) => r.id === a.id);
                  return (
                    <li key={a.id} className="p-2 flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleRefAttachment(a.id)}
                        className="mt-1 shrink-0"
                        data-testid={`compose-ref-${a.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-900 truncate" title={a.filename}>
                          {a.filename}
                        </div>
                        <div className="text-2xs text-zinc-500">
                          {a.category ? <span className="mr-1.5">{a.category}</span> : null}
                          {a.size ? <span>{Math.round(a.size / 1024)} KB</span> : null}
                          {a.document_date ? <span className="ml-1.5">· {a.document_date}</span> : null}
                        </div>
                        {summary && (
                          summary.ok ? (
                            <div className="text-2xs text-emerald-700 mt-0.5">
                              Read {summary.chars.toLocaleString()} characters into the draft
                              {summary.tags && summary.tags.length > 0 && (
                                <span className="ml-1.5 inline-flex gap-1">
                                  {summary.tags.map((t) => (
                                    <span
                                      key={t}
                                      className="inline-block px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 text-2xs uppercase tracking-wider"
                                    >
                                      {t}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="text-2xs text-amber-700 mt-0.5">
                              Skipped — {summary.reason}
                            </div>
                          )
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>


          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-3" data-testid="compose-notes-card">
            <SectionTitle>What to emphasise (optional)</SectionTitle>
            <Textarea
              value={form.extra_notes}
              onChange={(e) => setForm({ ...form, extra_notes: e.target.value })}
              placeholder="E.g. 'Highlight the improvement in fall-risk and knee ROM — GP asked to review after last MRI.'"
              rows={4}
              className="border-zinc-300 text-sm"
              data-testid="compose-extra-notes"
            />
            {currentTemplate?.recipient_type === "ndis" && (
              <Row label="Diagnosis (used in prompt)">
                <Input
                  value={form.diagnosis_hint}
                  onChange={(e) => setForm({ ...form, diagnosis_hint: e.target.value })}
                  placeholder="e.g. Parkinson's, ASD, Cerebral Palsy"
                  className="border-zinc-300"
                  data-testid="compose-dx-hint"
                />
              </Row>
            )}
            {signatures.length > 0 && (
              <Row label="Signature">
                <select
                  value={form.signature_id || ""}
                  onChange={(e) => setForm({ ...form, signature_id: e.target.value })}
                  className="border border-zinc-300 rounded-md h-9 px-2 text-sm bg-white w-full"
                  data-testid="compose-signature-picker"
                >
                  <option value="">
                    {`Use default${signatures.find((s) => s.is_default) ? ` — ${signatures.find((s) => s.is_default).name}` : ""}`}
                  </option>
                  {signatures.map((s) => (
                    <option key={s.id} value={s.id}>
                      {`${s.name}${s.title ? ` · ${s.title}` : ""}${s.is_default ? "  (default)" : ""}`}
                    </option>
                  ))}
                </select>
              </Row>
            )}
          </Card>

          {/* Delivery format toggle — printed letter vs email.
              Kept just above "Draft with AI" so the operator sets it
              once, right before generation. The choice also drives
              how the AI structures the salutation, address block and
              signoff, so we regenerate for a real preview. */}
          <div
            className="border border-zinc-200 bg-white rounded-md p-2.5"
            data-testid="compose-delivery-format"
          >
            <div className="flex items-center gap-2">
              <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold">
                Deliver as
              </div>
              <div className="ml-auto inline-flex rounded-md border border-zinc-300 overflow-hidden">
                {[
                  { v: "letter", label: "Letter" },
                  { v: "email",  label: "Email" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setForm({ ...form, delivery_format: opt.v })}
                    className={
                      "px-3 h-7 text-xs transition " +
                      (form.delivery_format === opt.v
                        ? "bg-zinc-900 text-white"
                        : "bg-white text-zinc-700 hover:bg-zinc-50")
                    }
                    data-testid={`compose-delivery-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="text-2xs text-zinc-500 mt-1.5 leading-relaxed">
              {form.delivery_format === "email" ? (
                <>Email body — no address block, "Hi Dr Smith," salutation,
                tight paragraphs, "Kind regards,". Good for follow-ups and
                progress notes.</>
              ) : (
                <>Printed letter — date + address block, "Dear Dr Smith,"
                salutation, full paragraphs, "Yours sincerely,". Most
                referrers still prefer this for GP plans and reports.</>
              )}
            </div>
          </div>

          <Button
            onClick={draftWithAi}
            disabled={drafting || !form.client_email.trim()}
            className="w-full bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="compose-draft-btn"
          >
            {drafting ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Drafting with AI…</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-1.5" /> Draft with AI</>
            )}
          </Button>
          {contextSummary && (
            <div className="text-2xs text-zinc-500 leading-relaxed bg-zinc-50 border border-zinc-200 rounded p-2" data-testid="compose-context-summary">
              Context used · SOAP: {contextSummary.soap_notes_count} · Q's: {contextSummary.questionnaires_count} ·
              {contextSummary.has_sbn ? " SbN ✓" : " SbN —"} ·
              {contextSummary.has_measurements ? " Measurements ✓" : " Measurements —"} ·
              Appts (12mo): {contextSummary.appointments_last_12mo}
              {modelUsed && <div className="mt-0.5 opacity-70">Model: {modelUsed}</div>}
            </div>
          )}
        </div>

        {/* Right column — draft editor + export */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none space-y-3" data-testid="compose-editor-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <SectionTitle>Draft</SectionTitle>
                <p className="text-2xs text-zinc-500 mt-1">
                  Markdown supported — <code>**bold**</code>, <code>*italic*</code>, <code># heading</code>, <code>* bullet</code>.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline" onClick={save} disabled={saving}
                  className="border-zinc-300"
                  data-testid="compose-save-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                  Save
                </Button>
                <Button
                  variant="outline" onClick={() => download("docx")}
                  disabled={!form.id || downloading === "docx"}
                  className="border-zinc-300"
                  data-testid="compose-docx-btn"
                >
                  {downloading === "docx" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}
                  DOCX
                </Button>
                <Button
                  variant="outline" onClick={openPreview}
                  disabled={!form.id || previewLoading}
                  className="border-zinc-300"
                  data-testid="compose-preview-btn"
                  title="Render and preview the exact PDF before downloading or emailing"
                >
                  {previewLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
                  Preview
                </Button>
                <Button
                  variant="outline" onClick={() => download("pdf")}
                  disabled={!form.id || downloading === "pdf"}
                  className="border-zinc-300"
                  data-testid="compose-pdf-btn"
                >
                  {downloading === "pdf" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}
                  PDF
                </Button>
                <Button
                  variant="outline" onClick={print}
                  disabled={!form.id || printing}
                  className="border-zinc-300"
                  data-testid="compose-print-btn"
                  title="Print this letter now"
                >
                  {printing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Printer className="h-4 w-4 mr-1.5" />}
                  Print
                </Button>
                <Button
                  onClick={() => setEmailOpen(true)}
                  disabled={!form.id || !form.body_markdown?.trim()}
                  className="bg-zinc-950 hover:bg-zinc-800 text-white"
                  data-testid="compose-email-btn"
                >
                  <Send className="h-4 w-4 mr-1.5" /> Email…
                </Button>
              </div>
            </div>
            <Row label="Subject line (for email)">
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="e.g. Care plan update for Jane Doe"
                className="border-zinc-300"
                data-testid="compose-subject"
              />
            </Row>
            <Textarea
              value={form.body_markdown}
              onChange={(e) => setForm({ ...form, body_markdown: e.target.value })}
              placeholder="Tap 'Draft with AI' to generate a first draft, or write from scratch here."
              rows={30}
              className="border-zinc-300 font-mono text-[0.8125rem] leading-relaxed"
              data-testid="compose-body"
            />
          </Card>
        </div>
      </div>

      {emailOpen && (
        <SendEmailDialog
          defaultTo={form.recipient_snapshot?.email || ""}
          defaultSubject={form.subject || ""}
          clientEmail={form.client_email}
          clientName={form.client_name}
          sending={emailing}
          onClose={() => setEmailOpen(false)}
          onSend={sendEmail}
        />
      )}

      {gpDialogOpen && (
        <QuickAddGpDialog
          onClose={() => setGpDialogOpen(false)}
          onSaved={async (gp) => {
            await refreshGps();
            setForm((f) => ({ ...f, recipient_gp_id: gp.id, recipient_snapshot: gp }));
            setGpDialogOpen(false);
            toast.success("GP added");
          }}
        />
      )}

      {/* Preflight PDF preview — same bytes the PDF download / email
          attachment would produce. Lets the operator eyeball
          letterhead chrome, provider numbers and signature before
          anything goes out. */}
      <Dialog open={!!previewUrl} onOpenChange={(v) => { if (!v) closePreview(); }}>
        <DialogContent
          className="max-w-5xl w-[90vw] h-[90vh] flex flex-col p-0"
          data-testid="letter-preview-dialog"
        >
          <DialogHeader className="px-5 pt-5 pb-2">
            <DialogTitle className="flex items-center justify-between">
              <span>PDF preview — {form.subject || "Letter"}</span>
              <span className="text-xs font-normal text-zinc-500">
                Rendered via the exact same engine used for download and email.
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-zinc-200">
            {previewUrl && (
              <iframe
                src={previewUrl}
                title="Letter PDF preview"
                className="w-full h-full border-0 bg-white"
                data-testid="letter-preview-iframe"
              />
            )}
          </div>
          <DialogFooter className="px-5 py-3 border-t">
            <Button
              variant="outline"
              onClick={closePreview}
              data-testid="letter-preview-close"
            >
              Close
            </Button>
            <Button
              onClick={() => { download("pdf"); closePreview(); }}
              className="bg-zinc-950 text-white hover:bg-zinc-800"
              data-testid="letter-preview-download"
            >
              <FileDown className="h-4 w-4 mr-1.5" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function suggestFilename(form, ext) {
  const parts = [
    form.client_name || form.client_email || "letter",
    form.template_id,
    (form.updated_at || new Date().toISOString()).slice(0, 10),
  ];
  return parts.join("_").replace(/[^a-z0-9_-]/gi, "_") + "." + ext;
}

function SectionTitle({ children }) {
  return <div className="text-2xs uppercase tracking-[0.15em] text-zinc-500 font-semibold">{children}</div>;
}
function Row({ label, children }) {
  return (
    <div>
      <Label className="text-2xs uppercase tracking-wider text-zinc-500 block mb-1">{label}</Label>
      {children}
    </div>
  );
}
function SourceToggle({ label, value, onChange, testId, disabled = false }) {
  return (
    <div className={`flex items-center justify-between gap-3 py-1 ${disabled ? "opacity-60" : ""}`}>
      <div className="text-sm text-zinc-800">{label}</div>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testId} disabled={disabled} />
    </div>
  );
}

function SendEmailDialog({
  defaultTo, defaultSubject, clientEmail, clientName, sending, onClose, onSend,
}) {
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(defaultSubject);
  const [coverNote, setCoverNote] = useState("");
  const [attachment, setAttachment] = useState("pdf");
  // Optional patient-record documents to ship alongside the letter.
  const [clientAttachments, setClientAttachments] = useState([]);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState(() => new Set());
  const [loadingAtt, setLoadingAtt] = useState(false);
  // Aliases pulled from the client's profile — auto-added to CC by
  // the backend but surfaced here so the operator sees exactly who
  // will receive the letter before hitting Send.
  const [profileAliases, setProfileAliases] = useState([]);
  const [ccDraft, setCcDraft] = useState("");

  useEffect(() => {
    if (!clientEmail) return;
    setLoadingAtt(true);
    api.get(`/health-record/${encodeURIComponent(clientEmail)}/attachments`)
      .then((r) => setClientAttachments(r.data?.attachments || r.data || []))
      .catch(() => setClientAttachments([]))
      .finally(() => setLoadingAtt(false));
    // Fetch profile aliases (best-effort — silent on 404).
    api.get(`/clients/${encodeURIComponent(clientEmail)}/profile`)
      .then((r) => setProfileAliases(r.data?.profile?.additional_emails || []))
      .catch(() => setProfileAliases([]));
  }, [clientEmail]);

  function toggleAttachment(id) {
    setSelectedAttachmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // Parse the operator's CC input on the fly for the preview list.
  const operatorCc = ccDraft
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="email-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Send letter by email</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <Row label="To">
            <Input value={to} onChange={(e) => setTo(e.target.value)} className="border-zinc-300" data-testid="email-to" />
          </Row>
          <Row label="Subject">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="border-zinc-300" data-testid="email-subject" />
          </Row>
          <Row label="Cover note (HTML supported — optional)">
            <Textarea value={coverNote} onChange={(e) => setCoverNote(e.target.value)} rows={4} className="border-zinc-300 text-sm" data-testid="email-cover" placeholder="Leave blank for the default cover message." />
          </Row>
          <Row label="Attach the letter as">
            <Select value={attachment} onValueChange={setAttachment}>
              <SelectTrigger className="border-zinc-300" data-testid="email-attach"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="docx">DOCX</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <Row label="Extra CC (comma-separated, optional)">
            <Input
              value={ccDraft}
              onChange={(e) => setCcDraft(e.target.value)}
              placeholder="case.manager@example.com, specialist@example.com"
              className="border-zinc-300"
              data-testid="email-cc-input"
            />
          </Row>
          {/* Recipients preview — mirrors exactly what the backend will
              build (primary To + profile aliases + operator CC) so
              the operator can confirm every inbox before hitting Send.
              Especially important for the child-with-two-parents case
              where the profile aliases are invisible unless surfaced. */}
          <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2.5"
               data-testid="recipients-preview">
            <div className="text-2xs uppercase tracking-wider text-zinc-500 font-medium mb-1.5">
              Will be sent to
            </div>
            <ul className="space-y-1 text-[0.75rem]">
              <li className="flex items-baseline gap-2"
                  data-testid="recipients-preview-to">
                <span className="w-8 shrink-0 text-2xs uppercase text-zinc-500 font-medium">To</span>
                <span className="font-mono text-zinc-800 break-all">
                  {to || <span className="italic text-zinc-400">no recipient</span>}
                </span>
              </li>
              {profileAliases.map((em) => (
                <li key={`alias-${em}`}
                    className="flex items-baseline gap-2"
                    data-testid={`recipients-preview-alias-${em}`}>
                  <span className="w-8 shrink-0 text-2xs uppercase text-zinc-500 font-medium">Cc</span>
                  <span className="font-mono text-zinc-800 break-all">{em}</span>
                  <span className="text-2xs text-zinc-500">alias on profile</span>
                </li>
              ))}
              {operatorCc.map((em) => (
                <li key={`op-${em}`}
                    className="flex items-baseline gap-2"
                    data-testid={`recipients-preview-cc-${em}`}>
                  <span className="w-8 shrink-0 text-2xs uppercase text-zinc-500 font-medium">Cc</span>
                  <span className="font-mono text-zinc-800 break-all">{em}</span>
                  <span className="text-2xs text-zinc-500">extra CC</span>
                </li>
              ))}
            </ul>
          </div>

          {clientEmail && (
            <div className="pt-2 border-t border-zinc-200">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                  Include client documents
                </Label>
                {selectedAttachmentIds.size > 0 && (
                  <span className="text-2xs text-emerald-700 font-medium" data-testid="email-selected-count">
                    {selectedAttachmentIds.size} selected
                  </span>
                )}
              </div>
              {loadingAtt ? (
                <div className="text-xs text-zinc-500 italic">Loading files for {clientName || clientEmail}…</div>
              ) : clientAttachments.length === 0 ? (
                <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-2">
                  No files uploaded to this client&apos;s record yet.
                </div>
              ) : (
                <ul className="border border-zinc-200 rounded-md divide-y divide-zinc-200 max-h-52 overflow-y-auto" data-testid="email-client-files">
                  {clientAttachments.map((a) => {
                    const active = selectedAttachmentIds.has(a.id);
                    return (
                      <li key={a.id} className="p-2 flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleAttachment(a.id)}
                          className="mt-1 shrink-0"
                          data-testid={`email-file-${a.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-zinc-900 truncate" title={a.filename}>
                            {a.filename}
                          </div>
                          <div className="text-2xs text-zinc-500">
                            {a.category ? <span className="mr-1.5">{a.category}</span> : null}
                            {a.size ? <span>{Math.round(a.size / 1024)} KB</span> : null}
                            {a.document_date ? <span className="ml-1.5">· {a.document_date}</span> : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            disabled={sending || !to}
            onClick={() => onSend({
              to,
              subject,
              cover_note: coverNote,
              attachment,
              extra_attachment_ids: Array.from(selectedAttachmentIds),
              cc: operatorCc,
            })}
            data-testid="email-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuickAddGpDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "Dr", first_name: "", last_name: "", clinic: "",
    address_line_1: "", suburb: "", state: "VIC", postcode: "",
    email: "", phone: "", provider_number: "",
  });
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="quick-gp-dialog">
        <DialogHeader><DialogTitle>Quick add GP</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Title"><Input value={form.title} onChange={change("title")} className="border-zinc-300" /></Row>
          <div />
          <Row label="First name*"><Input value={form.first_name} onChange={change("first_name")} className="border-zinc-300" data-testid="quick-gp-first" /></Row>
          <Row label="Last name*"><Input value={form.last_name} onChange={change("last_name")} className="border-zinc-300" data-testid="quick-gp-last" /></Row>
          <div className="col-span-2"><Row label="Clinic"><Input value={form.clinic} onChange={change("clinic")} className="border-zinc-300" data-testid="quick-gp-clinic" /></Row></div>
          <div className="col-span-2"><Row label="Address"><Input value={form.address_line_1} onChange={change("address_line_1")} className="border-zinc-300" data-testid="quick-gp-addr" /></Row></div>
          <Row label="Suburb"><Input value={form.suburb} onChange={change("suburb")} className="border-zinc-300" data-testid="quick-gp-suburb" /></Row>
          <Row label="State">
            <select
              value={form.state || ""}
              onChange={change("state")}
              className="w-full h-9 border border-zinc-300 rounded-md px-2 text-sm bg-white"
              data-testid="quick-gp-state"
            >
              {["VIC","NSW","QLD","WA","SA","TAS","ACT","NT"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Row>
          <Row label="Postcode"><Input value={form.postcode} onChange={change("postcode")} className="border-zinc-300" data-testid="quick-gp-postcode" /></Row>
          <Row label="Provider No.">
            <Input
              value={form.provider_number}
              onChange={change("provider_number")}
              placeholder="e.g. 1234567B"
              className="border-zinc-300"
              data-testid="quick-gp-provnum"
            />
          </Row>
          <div className="col-span-2"><Row label="Email"><Input value={form.email} onChange={change("email")} className="border-zinc-300" /></Row></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            disabled={busy || !form.first_name || !form.last_name}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await api.post("/letters/gps", form);
                onSaved(r.data);
              } catch (e) {
                toast.error("Add failed", { description: e?.response?.data?.detail || e.message });
              } finally {
                setBusy(false);
              }
            }}
            data-testid="quick-gp-save"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            Add and select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
